import React, { useEffect, useId, useRef, useState } from 'react'
import {
  fetchManagedCustomersForPicker,
  normalizeManagedCustomerItems,
} from '../lib/customerManagementApi'

const EMPTY_ITEMS = []

function formatPhone(phone) {
  const digits = String(phone ?? '').replace(/[^0-9]/g, '')
  if (!digits) return ''
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
}

function maskPhone(phone) {
  const formatted = formatPhone(phone)
  const digits = String(phone ?? '').replace(/[^0-9]/g, '')
  if (digits.length < 4) return formatted
  const last4 = digits.slice(-4)
  if (digits.length >= 11) return `${digits.slice(0, 3)}-****-${last4}`
  if (digits.length >= 10) return `${digits.slice(0, 3)}-***-${last4}`
  return `****-${last4}`
}

export default function ManagedCustomerAutocomplete({
  value,
  onValueChange,
  selectedCustomerId,
  onSelectCustomer,
  onClearCustomer,
  disabled = false,
  required = false,
  name,
  placeholder,
  inputClassName = 'w-full rounded border px-2 py-1.5 text-sm',
  size = 6,
  minSearchLength = 2,
}) {
  const inputId = useId()
  const listboxId = `${inputId}-listbox`
  const requestSeqRef = useRef(0)
  const blurTimerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState(EMPTY_ITEMS)
  const [loading, setLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const query = String(value || '').trim()
  const hasSelection = selectedCustomerId !== null && selectedCustomerId !== undefined && String(selectedCustomerId) !== ''
  const canSearch = query.length >= minSearchLength

  useEffect(() => {
    if (!open || disabled || !canSearch) {
      setItems(EMPTY_ITEMS)
      setLoading(false)
      setHighlightedIndex(-1)
      return undefined
    }

    const seq = requestSeqRef.current + 1
    requestSeqRef.current = seq
    setLoading(true)

    const timer = window.setTimeout(async () => {
      try {
        const res = await fetchManagedCustomersForPicker({ q: query, page: 0, size })
        if (requestSeqRef.current !== seq) return
        setItems(normalizeManagedCustomerItems(res))
        setHighlightedIndex(-1)
      } catch (_) {
        if (requestSeqRef.current !== seq) return
        setItems(EMPTY_ITEMS)
        setHighlightedIndex(-1)
      } finally {
        if (requestSeqRef.current === seq) setLoading(false)
      }
    }, 220)

    return () => {
      window.clearTimeout(timer)
    }
  }, [canSearch, disabled, open, query, size])

  useEffect(() => () => {
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current)
  }, [])

  const pickCustomer = (customer) => {
    if (!customer || disabled) return
    onSelectCustomer?.(customer)
    setOpen(false)
    setHighlightedIndex(-1)
  }

  const handleChange = (e) => {
    onValueChange?.(e.target.value)
    if (!open) setOpen(true)
  }

  const handleFocus = () => {
    if (disabled) return
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current)
    setOpen(true)
  }

  const handleBlur = () => {
    blurTimerRef.current = window.setTimeout(() => {
      setOpen(false)
      setHighlightedIndex(-1)
    }, 120)
  }

  const handleKeyDown = (e) => {
    if (!open || disabled) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.min(prev + 1, items.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
      return
    }
    if (e.key === 'Enter' && highlightedIndex >= 0 && items[highlightedIndex]) {
      e.preventDefault()
      pickCustomer(items[highlightedIndex])
      return
    }
    if (e.key === 'Escape') {
      setOpen(false)
      setHighlightedIndex(-1)
    }
  }

  const showList = open && !disabled && canSearch

  return (
    <div className="relative">
      <input
        type="text"
        name={name}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={inputClassName}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={highlightedIndex >= 0 ? `${listboxId}-${highlightedIndex}` : undefined}
      />

      {showList ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
        >
          {loading ? (
            <div className="px-3 py-2 text-xs text-slate-500">검색 중...</div>
          ) : items.length > 0 ? (
            items.map((item, idx) => {
              const selected = idx === highlightedIndex
              const phoneLabel = item.customerPhone ? maskPhone(item.customerPhone) : ''
              return (
                <button
                  key={item.id}
                  id={`${listboxId}-${idx}`}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left ${
                    selected ? 'bg-sky-50 text-brand-navy' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickCustomer(item)}
                >
                  <span className="min-w-0 truncate font-medium">{item.customerName || '이름 없음'}</span>
                  {phoneLabel ? <span className="shrink-0 text-xs text-slate-500">{phoneLabel}</span> : null}
                </button>
              )
            })
          ) : (
            <div className="px-3 py-2 text-xs text-slate-500">검색 결과 없음</div>
          )}
        </div>
      ) : null}

      {hasSelection ? (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="inline-flex items-center rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 font-medium text-brand-navy">
            기존 고객
          </span>
          {onClearCustomer ? (
            <button
              type="button"
              className="font-medium text-slate-500 hover:text-slate-800"
              onClick={onClearCustomer}
              disabled={disabled}
            >
              선택 해제
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
